import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPin, Zap, Star, Phone, Globe, ExternalLink, Bookmark,
  X, Filter, ChevronDown, RotateCcw, Lock, UserPlus,
  ArrowRight, Sparkles, CheckCircle2
} from 'lucide-react';
import { BUSINESS_CATEGORIES, RADIUS_OPTIONS, SORT_OPTIONS } from '../utils/constants';
import api from '../utils/api';

// ── Guest search tracking ──────────────────────────────────────────────────
const GUEST_KEY = 'lr_guest_done'; // set to '1' after first search is done
const hasUsedTrial = () => { try { return localStorage.getItem(GUEST_KEY)==='1'; } catch { return false; } };
const markTrialUsed = () => { try { localStorage.setItem(GUEST_KEY,'1'); } catch {} };

const DFLT = { location:'', radius:5000, businessTypes:[], keyword:'', minRating:0, statusFilter:'', hasPhone:false, hasWebsite:false };

function norm(l) {
  return { ...l, rating:l.rating!=null?Number(l.rating):null, lead_score:Number(l.lead_score)||0, review_count:Number(l.review_count||l.reviewCount)||0 };
}

// ── Shared styles ──────────────────────────────────────────────────────────
const S = {
  input:     { width:'100%', boxSizing:'border-box', height:44, background:'var(--surface)', border:'1.5px solid var(--border)', borderRadius:10, fontSize:14, color:'var(--text)', paddingLeft:40, paddingRight:36, outline:'none' },
  inputPlain:{ width:'100%', boxSizing:'border-box', height:36, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, fontSize:13, color:'var(--text)', padding:'0 12px', outline:'none' },
  select:    { width:'100%', boxSizing:'border-box', height:34, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, fontSize:12.5, color:'var(--text)', padding:'0 10px', outline:'none', cursor:'pointer' },
  chip:      { display:'inline-flex', alignItems:'center', height:28, padding:'0 11px', borderRadius:20, border:'1px solid var(--border)', background:'var(--bg3)', color:'var(--text2)', fontSize:12, cursor:'pointer', whiteSpace:'nowrap' },
  chipActive:{ display:'inline-flex', alignItems:'center', height:28, padding:'0 11px', borderRadius:20, border:'1px solid var(--accent)', background:'var(--accent)', color:'#fff', fontSize:12, cursor:'pointer', whiteSpace:'nowrap', fontWeight:600 },
  btnGhost:  { display:'inline-flex', alignItems:'center', gap:5, height:32, padding:'0 12px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--text2)', fontSize:12, cursor:'pointer', whiteSpace:'nowrap' },
  btnIcon:   { display:'inline-flex', alignItems:'center', justifyContent:'center', width:32, height:32, borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--text3)', cursor:'pointer' },
  badgeGreen:{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 7px', borderRadius:20, background:'var(--green-bg)', color:'var(--green)', fontSize:11, fontWeight:600, flexShrink:0 },
  badgeRed:  { display:'inline-flex', alignItems:'center', gap:4, padding:'2px 7px', borderRadius:20, background:'#fee2e2', color:'#dc2626', fontSize:11, fontWeight:600, flexShrink:0 },
  badgeGray: { display:'inline-flex', alignItems:'center', gap:4, padding:'2px 7px', borderRadius:20, background:'var(--bg3)', color:'var(--text3)', fontSize:11, fontWeight:600, flexShrink:0 },
  scoreTrack:{ flex:1, height:5, borderRadius:3, background:'var(--border)', overflow:'hidden' },
  emptyState:{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'60px 20px', gap:14, textAlign:'center', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14 },
  dropdown:  { position:'absolute', top:'100%', left:0, zIndex:200, marginTop:6, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, boxShadow:'0 12px 32px rgba(0,0,0,.14)', overflow:'hidden' },
  overlay:   { position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16 },
};

// ── LocationInput ──────────────────────────────────────────────────────────
function LocationInput({ value, onChange }) {
  const [q,setQ] = useState(value);
  const [sug,setSug] = useState([]);
  const [open,setOpen] = useState(false);
  const ref = useRef(null); const deb = useRef(null);
  useEffect(()=>{setQ(value);},[value]);
  useEffect(()=>{
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  },[]);
  const suggest = async v => {
    if (!v || v.length < 2) { setSug([]); return; }
    try {
      // plain fetch — bypasses axios interceptor that redirects 401 to /login
      const r = await fetch('/api/leads/location-search?q=' + encodeURIComponent(v));
      if (!r.ok) { setSug([]); return; }
      const data = await r.json();
      const res = data.results || [];
      setSug(res); setOpen(res.length > 0);
    } catch { setSug([]); }
  };
  return (
    <div ref={ref} style={{position:'relative',flex:'1 1 260px',minWidth:180}}>
      <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--text3)',pointerEvents:'none'}}><MapPin size={16}/></span>
      <input style={S.input} placeholder="Enter city, area or landmark…" value={q}
        onChange={e=>{const v=e.target.value;setQ(v);onChange(v);clearTimeout(deb.current);deb.current=setTimeout(()=>suggest(v),280);}}
        onFocus={()=>sug.length&&setOpen(true)}
        onKeyDown={e=>e.key==='Enter'&&onChange(q)}/>
      {q&&<button style={{position:'absolute',right:11,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--text3)',padding:2,display:'flex'}} onClick={()=>{setQ('');onChange('');setSug([]);}}><X size={13}/></button>}
      {open&&sug.length>0&&(
        <div style={{...S.dropdown,minWidth:'100%'}}>
          {sug.map((p,i)=>(
            <div key={p.place_id||i} style={{display:'flex',alignItems:'flex-start',gap:8,padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid var(--border)',fontSize:13}}
              onMouseDown={()=>{setQ(p.main_text);onChange(p.main_text);setSug([]);setOpen(false);}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--bg3)'}
              onMouseLeave={e=>e.currentTarget.style.background=''}>
              <MapPin size={13} style={{color:'var(--accent)',flexShrink:0,marginTop:2}}/>
              <div><div style={{color:'var(--text)',fontWeight:500}}>{p.main_text}</div>{p.secondary&&<div style={{fontSize:11.5,color:'var(--text3)',marginTop:1}}>{p.secondary}</div>}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── DropdownPill ──────────────────────────────────────────────────────────
function DropdownPill({label,active,badge,children,minW=220}) {
  const [open,setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(()=>{
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  },[]);
  return (
    <div ref={ref} style={{position:'relative',flexShrink:0}}>
      <button onClick={()=>setOpen(p=>!p)}
        style={{display:'inline-flex',alignItems:'center',gap:6,height:38,padding:'0 13px',borderRadius:9,border:`1.5px solid ${active?'var(--accent)':'var(--border)'}`,background:active?'var(--accent-bg)':'var(--surface)',color:active?'var(--accent)':'var(--text2)',fontSize:13,cursor:'pointer',fontWeight:active?700:500,whiteSpace:'nowrap'}}>
        {label}
        {badge>0&&<span style={{background:'var(--accent)',color:'#fff',borderRadius:8,padding:'0 5px',fontSize:10,fontWeight:700,lineHeight:'16px'}}>{badge}</span>}
        <ChevronDown size={13} style={{opacity:.6,transform:open?'rotate(180deg)':'none',transition:'transform .15s'}}/>
      </button>
      {open&&(
        <div style={{...S.dropdown,minWidth:minW,maxHeight:380,overflowY:'auto'}}>
          <div onClick={e=>e.stopPropagation()}>{children}</div>
          <div style={{padding:'8px 12px',borderTop:'1px solid var(--border)'}}>
            <button onClick={()=>setOpen(false)} style={{width:'100%',height:32,borderRadius:7,border:'none',background:'var(--accent)',color:'#fff',fontSize:12.5,fontWeight:600,cursor:'pointer'}}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SignupWall modal ───────────────────────────────────────────────────────
function SignupWall({ leadsFound, location, onClose }) {
  const navigate = useNavigate();
  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:'var(--surface)',borderRadius:20,width:'100%',maxWidth:460,overflow:'hidden',boxShadow:'0 24px 60px rgba(0,0,0,.35)'}}>
        {/* Gradient top */}
        <div style={{background:'linear-gradient(135deg,var(--accent) 0%,#7c3aed 100%)',padding:'30px 28px 22px',textAlign:'center'}}>
          <div style={{fontSize:44,marginBottom:12}}>🎯</div>
          <h2 style={{fontFamily:'var(--font-display)',fontSize:22,fontWeight:800,color:'#fff',marginBottom:8}}>
            You found <strong>{leadsFound} leads</strong>!
          </h2>
          <p style={{color:'rgba(255,255,255,.88)',fontSize:14,lineHeight:1.55,margin:0}}>
            Create your free account to save these leads, run campaigns, and keep searching in <strong>{location}</strong>.
          </p>
        </div>
        {/* Body */}
        <div style={{padding:'22px 26px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:11,marginBottom:22}}>
            {[
              ['🔍','Search any location','No credit card needed'],
              ['💾','Save & organize leads','Build your sales pipeline'],
              ['📧','Email + WhatsApp campaigns','Reach leads in one click'],
              ['📊','Lead scoring + CRM','Focus on the best prospects'],
            ].map(([icon,title,sub])=>(
              <div key={title} style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:38,height:38,borderRadius:10,background:'var(--accent-bg)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:17}}>{icon}</div>
                <div><div style={{fontWeight:600,fontSize:13.5}}>{title}</div><div style={{fontSize:12,color:'var(--text3)'}}>{sub}</div></div>
              </div>
            ))}
          </div>
          <button onClick={()=>navigate('/register')}
            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',height:48,borderRadius:11,border:'none',background:'var(--accent)',color:'#fff',fontWeight:800,fontSize:15.5,cursor:'pointer',marginBottom:10}}>
            <UserPlus size={17}/> Create Free Account <ArrowRight size={15}/>
          </button>
          <button onClick={()=>navigate('/login')}
            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',height:42,borderRadius:11,border:'1.5px solid var(--border)',background:'transparent',color:'var(--text2)',fontWeight:600,fontSize:14,cursor:'pointer'}}>
            Already have an account? Sign In
          </button>
          <div style={{textAlign:'center',fontSize:12,color:'var(--text3)',marginTop:12,display:'flex',alignItems:'center',justifyContent:'center',gap:12,flexWrap:'wrap'}}>
            {['Search free','Sign up to save','No card needed'].map(t=>(
              <span key={t} style={{display:'flex',alignItems:'center',gap:4}}><CheckCircle2 size={11} style={{color:'var(--green)'}}/>{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── LeadCard (blurred after index ≥ BLUR_FROM) ────────────────────────────
const BLUR_FROM = 5; // first 5 cards visible, rest blurred

function LeadCard({ lead, idx, onUnlock }) {
  const status  = lead.business_status||lead.status||'UNKNOWN';
  const rating  = lead.rating!=null?Number(lead.rating):null;
  const score   = Number(lead.lead_score)||0;
  const reviews = Number(lead.review_count||0);
  const badge   = status==='OPERATIONAL'?S.badgeGreen:status.includes('CLOSED')?S.badgeRed:S.badgeGray;
  const bTxt    = status==='OPERATIONAL'?'Open':status.includes('CLOSED')?'Closed':'Unknown';
  const blurred = idx >= BLUR_FROM;

  return (
    <div style={{position:'relative',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:11,padding:'13px 15px',display:'flex',flexDirection:'column',gap:9,overflow:'hidden'}}>
      {blurred&&(
        <div style={{position:'absolute',inset:0,backdropFilter:'blur(7px)',WebkitBackdropFilter:'blur(7px)',background:'rgba(255,255,255,.55)',zIndex:2,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:10,borderRadius:11}}>
          <div style={{width:44,height:44,borderRadius:'50%',background:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center'}}><Lock size={20} style={{color:'#fff'}}/></div>
          <div style={{fontWeight:700,fontSize:13.5,textAlign:'center',color:'var(--text)'}}>Sign in or sign up</div>
          <div style={{fontSize:12,color:'var(--text3)',textAlign:'center'}}>Save leads & run campaigns</div>
          <button onClick={onUnlock} style={{display:'flex',alignItems:'center',gap:6,padding:'7px 18px',borderRadius:8,border:'none',background:'var(--accent)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>
            <UserPlus size={13}/> Sign Up or Login
          </button>
        </div>
      )}
      <div style={{display:'flex',alignItems:'flex-start',gap:9}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:13.5,lineHeight:1.3,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{lead.name}</div>
          <div style={{display:'flex',alignItems:'center',gap:4,fontSize:11.5,color:'var(--text3)'}}><MapPin size={10} style={{flexShrink:0}}/><span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{lead.address||lead.vicinity||'N/A'}</span></div>
        </div>
        <span style={badge}><span style={{width:6,height:6,borderRadius:'50%',background:status==='OPERATIONAL'?'var(--green)':status.includes('CLOSED')?'#dc2626':'var(--text3)',flexShrink:0,display:'inline-block'}}/>{bTxt}</span>
      </div>
      {rating!=null&&rating>0&&(
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{display:'flex',alignItems:'center',gap:4,fontWeight:700,fontSize:12.5,color:'var(--gold)'}}><Star size={11} fill="var(--gold)" stroke="none"/>{rating.toFixed(1)}</div>
          <span style={{fontSize:11.5,color:'var(--text3)'}}>{reviews.toLocaleString()} reviews</span>
          {score>0&&<div style={{display:'flex',alignItems:'center',gap:5,flex:1}}><div style={S.scoreTrack}><div style={{height:'100%',borderRadius:3,width:`${score}%`,background:score>=70?'var(--green)':score>=40?'var(--gold)':'#ef4444'}}/></div><span style={{fontSize:10.5,fontWeight:700,color:score>=70?'var(--green)':score>=40?'var(--gold)':'#ef4444'}}>{score}</span></div>}
        </div>
      )}
      <div style={{display:'flex',flexDirection:'column',gap:4}}>
        {lead.phone&&<div style={{display:'flex',alignItems:'center',gap:7,fontSize:12.5}}><Phone size={11} style={{color:'var(--accent)',flexShrink:0}}/><a href={`tel:${lead.phone}`} style={{color:'var(--text2)',textDecoration:'none'}}>{lead.phone}</a></div>}
        {lead.email&&<div style={{display:'flex',alignItems:'center',gap:7,fontSize:12.5}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
          <a href={`mailto:${lead.email}`} style={{color:'var(--accent)',textDecoration:'none',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{lead.email}</a>
        </div>}
        {!lead.phone&&!lead.email&&<div style={{fontSize:12,color:'var(--text3)'}}>No contact details</div>}
      </div>
    </div>
  );
}

// ── TrySearchPage ──────────────────────────────────────────────────────────
export default function TrySearchPage() {
  const navigate = useNavigate();
  const [filters,setFilters] = useState(() => {
    const loc = sessionStorage.getItem('lr_try_location')||'';
    if (loc) sessionStorage.removeItem('lr_try_location');
    return { ...DFLT, location: loc };
  });
  const [leads,setLeads]     = useState([]);
  const [loading,setLoading] = useState(false);
  const [loadMsg,setLoadMsg] = useState('');
  const [showWall,setWall]   = useState(false);
  const [searched,setSearched]= useState(false);

  // Auto-search if location was pre-filled from landing page
  useEffect(() => {
    if (filters.location && !hasUsedTrial()) {
      doSearch();
    }
  }, []); // eslint-disable-line

  const sel = filters.businessTypes?.length||0;
  const toggle = t => { const c=filters.businessTypes||[]; setFilters(f=>({...f,businessTypes:c.includes(t)?c.filter(x=>x!==t):[...c,t]})); };

  const doSearch = async () => {
    if (!filters.location) return;

    // Already used trial → show wall immediately
    if (hasUsedTrial()) { setWall(true); return; }

    setLoading(true); setLeads([]);
    const msgs = ['📍 Finding location…','🔍 Searching businesses…','📋 Getting contact details…','✅ Done!'];
    let mi=0;
    const iv = setInterval(()=>{ setLoadMsg(msgs[Math.min(mi++,msgs.length-1)]); },2000);
    try {
      // plain fetch — bypasses axios interceptor that redirects 401 to /login
      const resp = await fetch('/api/leads/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...filters, page: 1, limit: 20 }),
      });
      const data = await resp.json();
      const found = (data.leads || []).map(norm);
      setLeads(found);
      setSearched(true);
      markTrialUsed(); // consume the 1 free search
      // Show wall after 4s so user can see results first
      if (found.length > 0) setTimeout(()=>setWall(true), 4000);
    } catch { setLeads([]); }
    finally { clearInterval(iv); setLoading(false); setLoadMsg(''); }
  };

  const busy = loading||!filters.location;

  return (
    <div style={{minHeight:'100vh',background:'var(--bg)',fontFamily:'inherit'}}>
      <style>{`
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .try-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(290px,1fr)); gap:12px; }
        @media(max-width:600px){ .try-grid{grid-template-columns:1fr;} }
        @media(min-width:480px) and (max-width:600px){ .try-grid{grid-template-columns:repeat(2,1fr);} }
      `}</style>

      {/* ── Navbar ── */}
      <div style={{position:'sticky',top:0,zIndex:50,background:'var(--surface)',borderBottom:'1px solid var(--border)',padding:'0 20px',height:56,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <Link to="/" style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:18,textDecoration:'none',color:'var(--text)',display:'flex',alignItems:'center',gap:8}}>
          <span>📡</span>LeadRadar
        </Link>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:12.5,color:'var(--text3)',display:'flex',alignItems:'center',gap:5}}>
            <Sparkles size={13} style={{color:'var(--accent)'}}/> Search Free
          </span>
          <Link to="/login" style={{display:'inline-flex',alignItems:'center',height:34,padding:'0 13px',borderRadius:8,border:'1px solid var(--border)',background:'transparent',color:'var(--text2)',fontWeight:500,fontSize:13,textDecoration:'none'}}>Sign In</Link>
          <Link to="/register" style={{display:'inline-flex',alignItems:'center',gap:5,height:34,padding:'0 14px',borderRadius:8,background:'var(--accent)',color:'#fff',fontWeight:700,fontSize:13,textDecoration:'none'}}>
            <UserPlus size={13}/> Sign Up or Login
          </Link>
        </div>
      </div>

      {/* ── Page body ── */}
      <div style={{maxWidth:1200,margin:'0 auto',padding:'24px 16px'}}>

        {/* Hero banner */}
        <div style={{background:'linear-gradient(135deg,var(--accent-bg),#f3e8ff)',border:'1.5px solid var(--accent)',borderRadius:16,padding:'20px 24px',marginBottom:20,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:180}}>
            <div style={{fontFamily:'var(--font-display)',fontSize:17,fontWeight:800,color:'var(--accent)',marginBottom:5}}>🎯 Try LeadRadar Free — No Login Needed</div>
            <div style={{fontSize:13.5,color:'var(--text2)',lineHeight:1.55}}>Search any city and get real business leads with contact details. No login needed to search — sign up to save and reach out.</div>
          </div>
          <Link to="/register" style={{display:'inline-flex',alignItems:'center',gap:7,padding:'10px 20px',borderRadius:10,background:'var(--accent)',color:'#fff',fontWeight:700,fontSize:14,textDecoration:'none',flexShrink:0}}>
            <UserPlus size={15}/> Sign Up to Save Leads
          </Link>
        </div>

        {/* ── Filter bar ── */}
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'14px 16px',marginBottom:16,display:'flex',flexDirection:'column',gap:10}}>
          {/* Row 1: location + search */}
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <LocationInput value={filters.location} onChange={v=>setFilters(f=>({...f,location:v}))}/>
            <button onClick={doSearch} disabled={busy}
              style={{display:'flex',alignItems:'center',gap:7,height:44,padding:'0 22px',borderRadius:10,border:'none',fontWeight:700,fontSize:14.5,cursor:busy?'not-allowed':'pointer',background:busy?'var(--border)':'var(--accent)',color:busy?'var(--text3)':'#fff',flexShrink:0,whiteSpace:'nowrap'}}>
              {loading
                ? <><span style={{width:15,height:15,border:'2px solid #fff',borderTopColor:'transparent',borderRadius:'50%',display:'inline-block',animation:'spin .7s linear infinite'}}/> Searching…</>
                : <><Zap size={15}/> Search Leads</>}
            </button>
            {(sel>0||filters.radius!==5000||filters.minRating>0||filters.hasPhone||filters.hasWebsite)&&(
              <button onClick={()=>setFilters(DFLT)} style={{...S.btnGhost,height:44,padding:'0 14px',flexShrink:0}}><RotateCcw size={13}/> Reset</button>
            )}
          </div>

          {/* Row 2: filter pills */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            {/* Radius */}
            <DropdownPill label={`📍 ${RADIUS_OPTIONS.find(r=>r.value===filters.radius)?.label||'5km'}`} active={filters.radius!==5000} badge={0} minW={200}>
              <div style={{padding:'12px 14px'}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.9,color:'var(--text3)',marginBottom:8}}>Search Radius</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {RADIUS_OPTIONS.map(o=><button key={o.value} style={{...(filters.radius===o.value?S.chipActive:S.chip)}} onClick={()=>setFilters(f=>({...f,radius:o.value}))}>{o.label}</button>)}
                </div>
              </div>
            </DropdownPill>

            {/* Category */}
            <DropdownPill label="🏢 Category" active={sel>0} badge={sel} minW={270}>
              <div style={{padding:'12px 14px'}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.9,color:'var(--text3)',marginBottom:10}}>Business Type {sel>0&&<span style={{color:'var(--accent)'}}>· {sel} selected</span>}</div>
                {BUSINESS_CATEGORIES.map(cat=>{
                  const catSel=(filters.businessTypes||[]).filter(t=>cat.types.map(x=>x.value).includes(t)).length;
                  return(
                    <div key={cat.label} style={{marginBottom:10}}>
                      <div style={{fontSize:12,fontWeight:700,color:catSel>0?'var(--accent)':'var(--text2)',marginBottom:5,display:'flex',alignItems:'center',gap:6}}>
                        {cat.icon} {cat.label}{catSel>0&&<span style={{background:'var(--accent)',color:'#fff',borderRadius:8,padding:'0 5px',fontSize:9,fontWeight:700}}>{catSel}</span>}
                      </div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                        {cat.types.map(t=><button key={t.value} style={{...(filters.businessTypes||[]).includes(t.value)?S.chipActive:S.chip,height:24,fontSize:11,padding:'0 8px'}} onClick={()=>toggle(t.value)}>{t.label}</button>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </DropdownPill>

            {/* Rating */}
            <DropdownPill label={filters.minRating>0?`⭐ ${filters.minRating}★+`:'⭐ Rating'} active={filters.minRating>0} badge={0} minW={180}>
              <div style={{padding:'12px 14px'}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.9,color:'var(--text3)',marginBottom:8}}>Min Rating</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {[[0,'Any'],[3,'3★+'],[3.5,'3.5★+'],[4,'4★+'],[4.5,'4.5★+']].map(([v,l])=><button key={v} style={{...(filters.minRating===v?S.chipActive:S.chip)}} onClick={()=>setFilters(f=>({...f,minRating:v}))}>{l}</button>)}
                </div>
              </div>
            </DropdownPill>

            {/* More filters */}
            <DropdownPill label="🔧 More" active={filters.hasPhone||filters.hasWebsite||!!filters.statusFilter||!!filters.keyword} badge={[filters.hasPhone,filters.hasWebsite,!!filters.statusFilter,!!filters.keyword].filter(Boolean).length} minW={230}>
              <div style={{padding:'14px 16px',display:'flex',flexDirection:'column',gap:12}}>
                <div>
                  <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.9,color:'var(--text3)',marginBottom:7}}>Keyword</div>
                  <input style={S.inputPlain} placeholder="e.g. digital marketing…" value={filters.keyword} onChange={e=>setFilters(f=>({...f,keyword:e.target.value}))}/>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.9,color:'var(--text3)',marginBottom:7}}>Status</div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {[['','All'],['OPERATIONAL','Open only'],['CLOSED_TEMPORARILY','Temp Closed']].map(([v,l])=><button key={v} style={{...(filters.statusFilter===v?S.chipActive:S.chip),height:26,fontSize:11.5}} onClick={()=>setFilters(f=>({...f,statusFilter:v}))}>{l}</button>)}
                  </div>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.9,color:'var(--text3)',marginBottom:7}}>Contact Info</div>
                  <div style={{display:'flex',gap:14}}>
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,cursor:'pointer',color:'var(--text2)'}}><input type="checkbox" style={{accentColor:'var(--accent)'}} checked={filters.hasPhone||false} onChange={e=>setFilters(f=>({...f,hasPhone:e.target.checked}))}/>📞 Has Phone</label>
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,cursor:'pointer',color:'var(--text2)'}}><input type="checkbox" style={{accentColor:'var(--accent)'}} checked={filters.hasWebsite||false} onChange={e=>setFilters(f=>({...f,hasWebsite:e.target.checked}))}/>🌐 Website</label>
                  </div>
                </div>
              </div>
            </DropdownPill>

            {/* Active chips */}
            {filters.hasPhone&&<span style={{...S.chipActive,gap:4}}>📞 Phone<button style={{background:'none',border:'none',color:'#fff',cursor:'pointer',padding:0,display:'flex'}} onClick={()=>setFilters(f=>({...f,hasPhone:false}))}><X size={10}/></button></span>}
            {filters.hasWebsite&&<span style={{...S.chipActive,gap:4}}>🌐 Website<button style={{background:'none',border:'none',color:'#fff',cursor:'pointer',padding:0,display:'flex'}} onClick={()=>setFilters(f=>({...f,hasWebsite:false}))}><X size={10}/></button></span>}
            {filters.statusFilter&&<span style={{...S.chipActive,gap:4}}>{filters.statusFilter==='OPERATIONAL'?'Open Only':'Temp Closed'}<button style={{background:'none',border:'none',color:'#fff',cursor:'pointer',padding:0,display:'flex'}} onClick={()=>setFilters(f=>({...f,statusFilter:''}))}><X size={10}/></button></span>}
          </div>
        </div>

        {/* ── Results toolbar ── */}
        {leads.length>0&&(
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'9px 14px',marginBottom:12,animation:'fadeUp .3s ease'}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:13,color:'var(--text3)'}}>
                <strong style={{color:'var(--text)'}}>{leads.length}</strong> leads found in <strong>{filters.location}</strong>
                <span style={{fontSize:11.5,color:'var(--text3)',marginLeft:6}}>
                  (sign in to see all)
                </span>
              </span>
            </div>
            <button onClick={()=>setWall(true)}
              style={{display:'inline-flex',alignItems:'center',gap:5,height:30,padding:'0 14px',borderRadius:7,border:'none',background:'var(--accent)',color:'#fff',fontSize:12.5,fontWeight:700,cursor:'pointer'}}>
              <Lock size={11}/> Sign In to Save All
            </button>
          </div>
        )}

        {/* Loading */}
        {loading&&(
          <div style={S.emptyState}>
            <div style={{width:38,height:38,border:'3px solid var(--border)',borderTopColor:'var(--accent)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
            <div style={{fontWeight:600,fontSize:15}}>{loadMsg||'Searching…'}</div>
            <div style={{fontSize:13,color:'var(--text3)'}}>Finding businesses near you</div>
          </div>
        )}

        {/* Empty / welcome */}
        {!loading&&leads.length===0&&!searched&&(
          <div style={{...S.emptyState,animation:'fadeUp .4s ease'}}>
            <div style={{fontSize:48}}>📡</div>
            <div style={{fontWeight:700,fontSize:19}}>Search businesses for free</div>
            <div style={{fontSize:14,color:'var(--text3)',maxWidth:320,lineHeight:1.6}}>Enter any city or area above, pick a category, and click Search Leads. Sign up to save results.</div>
            <div style={{display:'flex',gap:16,fontSize:13,color:'var(--text3)',flexWrap:'wrap',justifyContent:'center',marginTop:4}}>
              {['Real contact details','Phone & email','Sign up to save'].map(t=><span key={t} style={{display:'flex',alignItems:'center',gap:5}}><CheckCircle2 size={13} style={{color:'var(--green)'}}/>{t}</span>)}
            </div>
          </div>
        )}

        {/* Lead cards */}
        {!loading&&leads.length>0&&(
          <div className="try-grid" style={{animation:'fadeUp .3s ease'}}>
            {leads.map((lead,idx)=>(
              <LeadCard key={lead.id||lead.place_id} lead={lead} idx={idx} onUnlock={()=>setWall(true)}/>
            ))}
          </div>
        )}

        {/* Bottom CTA after results */}
        {!loading&&leads.length>0&&(
          <div style={{background:'linear-gradient(135deg,var(--accent),#7c3aed)',borderRadius:16,padding:'28px 24px',textAlign:'center',color:'#fff',marginTop:20,animation:'fadeUp .4s ease .1s both'}}>
            <div style={{fontSize:22,marginBottom:10}}>🔓 Sign Up to Save These {leads.length} Leads</div>
            <div style={{fontSize:14,opacity:.9,marginBottom:20,lineHeight:1.55}}>
              Create an account to save these leads, search more cities, and send email & WhatsApp campaigns.
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
              <button onClick={()=>navigate('/register')} style={{display:'flex',alignItems:'center',gap:8,padding:'12px 26px',borderRadius:11,border:'none',background:'#fff',color:'var(--accent)',fontWeight:800,fontSize:15,cursor:'pointer'}}>
                <UserPlus size={16}/> Sign Up Free
              </button>
              <button onClick={()=>navigate('/login')} style={{display:'flex',alignItems:'center',gap:8,padding:'12px 26px',borderRadius:11,border:'2px solid rgba(255,255,255,.5)',background:'transparent',color:'#fff',fontWeight:600,fontSize:15,cursor:'pointer'}}>
                Sign In
              </button>
            </div>
            <div style={{fontSize:12,opacity:.7,marginTop:14}}>Free to search · Sign up to save · No credit card</div>
          </div>
        )}
      </div>

      {/* Signup wall modal */}
      {showWall&&<SignupWall leadsFound={leads.length} location={filters.location||'this location'} onClose={()=>setWall(false)}/>}
    </div>
  );
}